import { Account } from '@/domain/usecases'
import { OnboardingOptions } from '@/domain/usecases/onboarding/onboarding-options'
import { UserOnboarding } from '@/domain/usecases/onboarding/user-onboarding'
import { UserProfileAssessment } from '@/domain/usecases/profile-assessment/user-profile-assessment'
import { FeatureFlag } from '@/infra/feature-flag/feature-flag'
import { makeSkillWebContentPageRedirectUrl } from '@/main/factories/http'
import { LDClient } from 'launchdarkly-react-client-sdk'
import { QueryClient } from 'react-query'
import { LoaderFunctionArgs, redirect } from 'react-router-dom'
import RegisteredPlanBaseLoader from '../registered-plan-base-loader'
import {
  contentDetailsPromise,
  enrollmentContentInfoPromise
} from './content-loader/content-loader-promises'

export default class ContentEnrollmentLoader extends RegisteredPlanBaseLoader {
  private static readonly ID_PATH_PARAM_NAME = 'id'

  constructor(
    readonly account: Account.Model,
    readonly queryClient: QueryClient,
    readonly ldClient: LDClient | undefined
  ) {
    super(account, queryClient, ldClient, FeatureFlag.Key.COMPANIES_ON_ENROLLMENT_ROLLOUT)
  }

  async getUserOnboarding() {
    return super.importRemoteModule('makeRemoteUserOnboarding').then(async makeRemoteOnboarding => {
      const remoteOnboarding = makeRemoteOnboarding(this.queryClient) as UserOnboarding
      return remoteOnboarding.get()
    })
  }

  async getUserProfileAssessment() {
    return super
      .importRemoteModule('makeRemoteUserProfileAssessment')
      .then(async makeRemoteUserProfileAssessment => {
        const remoteUserProfileAssessment = makeRemoteUserProfileAssessment(
          this.queryClient
        ) as UserProfileAssessment
        return remoteUserProfileAssessment.getLatest()
      })
  }

  async loader({ params }: LoaderFunctionArgs) {
    const id = parseInt(params[ContentEnrollmentLoader.ID_PATH_PARAM_NAME] ?? '')
    if (isNaN(id) || id <= 0) return null

    const userEnrollmentContentInfo = await enrollmentContentInfoPromise(this.queryClient, id, true)
    if (!userEnrollmentContentInfo || userEnrollmentContentInfo.restricted) {
      return redirect(makeSkillWebContentPageRedirectUrl(id))
    }

    return this.defer({
      data: Promise.all([
        super.importRemoteModule('makeRemoteAccountProfile').then(async makeRemote => {
          return makeRemote(this.queryClient) as Account
        }),
        super
          .importRemoteModule('makeRemoteOnboardingOptions')
          .then(async makeRemoteOnboardingOptions => {
            const remoteOnboardingOptions = makeRemoteOnboardingOptions(
              this.queryClient
            ) as OnboardingOptions
            return remoteOnboardingOptions['getStep4']()
          }),
        contentDetailsPromise(this.queryClient, id, true),
        Promise.allSettled([this.getUserOnboarding(), this.getUserProfileAssessment()]).then(
          items => items.map(item => (item.status == 'fulfilled' ? item.value : null))
        )
      ])
        .then(
          ([
            remoteAccountProfile,
            onboardingOptions,
            userContentDetails,
            [userOnboarding, userProfileAssessment]
          ]) => ({
            remoteAccountProfile,
            onboardingOptions,
            userContentDetails,
            userEnrollmentContentInfo,
            userOnboarding: userOnboarding as UserOnboarding.Model | null,
            userProfileAssessment: userProfileAssessment as UserProfileAssessment.Model | null
          })
        )
        .then(
          async ({
            remoteAccountProfile,
            onboardingOptions,
            userContentDetails,
            userEnrollmentContentInfo,
            userOnboarding,
            userProfileAssessment
          }) => {
            if (userEnrollmentContentInfo.restricted) {
              return redirect(makeSkillWebContentPageRedirectUrl(id))
            } else {
              return {
                remoteAccountProfile,
                accountProfile: await remoteAccountProfile.loader(),
                onboardingOptions,
                userContentDetails,
                userEnrollmentContentInfo,
                userSkills: userOnboarding?.interest?.skills || [],
                userProfileAssessment
              }
            }
          }
        )
    })
  }
}
